import React, { useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  Copy,
  FolderPlus,
  Trash2,
  GripVertical,
  Check,
  Edit3,
  Layers,
  ChevronRight,
  Eye,
  Plus,
  Lock,
  ExternalLink,
} from 'lucide-react';
import type { BlockNode, ViewportMode, StyleTargetState } from '../types';
import { getBlockRenderer } from '../blocks/render';
import { findPathToNode } from '../utils';
import { BLOCK_MANIFESTS } from '../blocks/generated/manifest';
import { CanvasToolsOverlay } from './CanvasToolsOverlay';
import { CanvasRulers } from './CanvasRulers';
import { resolveCanvasNodeStyle, generateCanvasDynamicCSS } from '../utils/styleResolver';

export interface CanvasViewportProps {
  viewport: ViewportMode;
  rootNode: BlockNode;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  mode?: 'edit' | 'preview';
  siteSlug?: string;
  pagePath?: string;
  activeState?: StyleTargetState;
  showOutlines?: boolean;
  showSpacingGuides?: boolean;
  highlightedBoxPart?: 'margin' | 'padding' | null;
  zoom?: number;
  onInsertNodeAt?: (targetParentId: string, node: BlockNode, index?: number) => void;
  onMoveNode?: (nodeId: string, direction: 'up' | 'down') => void;
  onMoveNodeTo?: (sourceId: string, destParentId: string, destIndex: number) => void;
  onDuplicateNode?: (nodeId: string) => void;
  onWrapInContainer?: (nodeId: string) => void;
  onDeleteNode?: (nodeId: string) => void;
  onUpdateProps?: (nodeId: string, props: Record<string, unknown>) => void;
  onResizeNode?: (nodeId: string, patch: { width?: string; height?: string; minHeight?: string }) => void;
  onOpenSectionPicker?: (insertIndex?: number) => void;
}

const VIEWPORT_WIDTHS: Record<ViewportMode, string> = {
  desktop: 'w-full max-w-[1200px]',
  tablet: 'w-[768px]',
  mobile: 'w-[375px]',
};

interface DropTargetState {
  parentId: string;
  index: number;
  position: 'before' | 'after' | 'inside';
}

export function CanvasViewport({
  viewport,
  rootNode,
  selectedNodeId,
  onSelectNode,
  mode = 'edit',
  siteSlug = 'academy',
  pagePath = '/',
  activeState = 'default',
  showOutlines = true,
  showSpacingGuides = false,
  highlightedBoxPart = null,
  zoom = 1,
  onInsertNodeAt,
  onMoveNode,
  onMoveNodeTo,
  onDuplicateNode,
  onWrapInContainer,
  onDeleteNode,
  onUpdateProps,
  onResizeNode,
  onOpenSectionPicker,
}: CanvasViewportProps): React.JSX.Element {
  const isPreview = mode === 'preview';

  // Active drop indicator state
  const [activeDrop, setActiveDrop] = useState<DropTargetState | null>(null);

  // Inline editing state: nodeId -> propName
  const [inlineEditingNodeId, setInlineEditingNodeId] = useState<string | null>(null);
  const [inlineTextValue, setInlineTextValue] = useState<string>('');
  const [inlinePropName, setInlinePropName] = useState<string>('text');

  // Breadcrumbs for selected node
  const breadcrumbNodes = selectedNodeId ? findPathToNode(rootNode, selectedNodeId) : [];
  const selectedNode = selectedNodeId ? breadcrumbNodes[breadcrumbNodes.length - 1] || null : null;
  const parentNode = selectedNodeId && breadcrumbNodes.length > 1 ? breadcrumbNodes[breadcrumbNodes.length - 2] : null;

  // Parse and handle drop event
  const handleDrop = (e: React.DragEvent, parentId: string, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDrop(null);

    try {
      const rawData = e.dataTransfer.getData('application/json');
      if (!rawData) return;
      const data = JSON.parse(rawData);

      if (data.kind === 'new_block' && onInsertNodeAt) {
        const manifest = BLOCK_MANIFESTS[data.type as keyof typeof BLOCK_MANIFESTS];
        const newNode: BlockNode = {
          id: `${data.type.toLowerCase()}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`,
          type: data.type,
          category: data.category,
          props: {
            text: `${manifest?.title || data.type} Block`,
            headline: manifest?.title || `Academic Excellence`,
            subheadline: `Empowering students to achieve top ranks in national entrance examinations.`,
            title: manifest?.title || data.type,
            label: manifest?.title || data.type,
          },
          styles: { base: {} },
        };
        onInsertNodeAt(parentId, newNode, index);
        onSelectNode(newNode.id);
      } else if (data.kind === 'move_block' && onMoveNodeTo) {
        onMoveNodeTo(data.sourceId, parentId, index);
      }
    } catch {
      // Ignored
    }
  };

  const handleDragOver = (e: React.DragEvent, parentId: string, index: number, position: 'before' | 'after' | 'inside') => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setActiveDrop({ parentId, index, position });
  };

  const startInlineEdit = (node: BlockNode) => {
    const propKey =
      node.props?.headline !== undefined
        ? 'headline'
        : node.props?.title !== undefined
        ? 'title'
        : node.props?.text !== undefined
        ? 'text'
        : 'label';
    setInlinePropName(propKey);
    setInlineTextValue(String(node.props?.[propKey] || ''));
    setInlineEditingNodeId(node.id);
  };

  const commitInlineEdit = () => {
    if (inlineEditingNodeId && onUpdateProps) {
      onUpdateProps(inlineEditingNodeId, { [inlinePropName]: inlineTextValue });
    }
    setInlineEditingNodeId(null);
  };

  // Recursive block renderer
  const renderNode = (node: BlockNode, parentNode: BlockNode | null, childIndex: number): React.JSX.Element => {
    const isSelected = node.id === selectedNodeId && !isPreview;
    const isEditingInline = node.id === inlineEditingNodeId && !isPreview;
    const Renderer = getBlockRenderer(node.type);
    const blockTitle = BLOCK_MANIFESTS[node.type as keyof typeof BLOCK_MANIFESTS]?.title || node.type;

    const isContainerLike =
      node.type === 'Container' ||
      node.type === 'Section' ||
      node.type === 'Grid2Col' ||
      node.children !== undefined;

    const renderedChildren =
      node.children && node.children.length > 0
        ? node.children.map((child, idx) => renderNode(child, node, idx))
        : undefined;

    // Is drop active immediately before this node
    const isDropBefore =
      activeDrop &&
      parentNode &&
      activeDrop.parentId === parentNode.id &&
      activeDrop.index === childIndex &&
      activeDrop.position === 'before';

    // Is drop active immediately after this node
    const isDropAfter =
      activeDrop &&
      parentNode &&
      activeDrop.parentId === parentNode.id &&
      activeDrop.index === childIndex + 1 &&
      activeDrop.position === 'after';

    // Root canvas node
    if (node.id === rootNode.id) {
      return (
        <div
          key={node.id}
          className="wb-root-canvas w-full min-h-[550px] relative transition-all"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isPreview) {
              onSelectNode(node.id);
            }
          }}
          onDragOver={(e) => {
            if (isPreview) return;
            handleDragOver(e, node.id, node.children?.length || 0, 'inside');
          }}
          onDragLeave={() => setActiveDrop(null)}
          onDrop={(e) => {
            if (isPreview) return;
            handleDrop(e, node.id, node.children?.length || 0);
          }}
        >
          <Renderer node={node} isEditor={!isPreview}>
            {renderedChildren}
          </Renderer>

          {/* Empty root container drop hint */}
          {(!node.children || node.children.length === 0) && !isPreview && (
            <div className="border-2 border-dashed border-border/80 rounded-2xl p-12 text-center flex flex-col items-center justify-center gap-3 bg-muted/10 my-8 mx-4">
              <Plus className="h-8 w-8 text-primary/60" />
              <div className="text-sm font-semibold text-foreground">Canvas is Empty</div>
              <p className="text-xs text-muted-foreground max-w-sm">
                Choose a turnkey section below or drag blocks from the library rail on the left.
              </p>
              {onOpenSectionPicker && (
                <button
                  type="button"
                  onClick={() => onOpenSectionPicker(0)}
                  className="mt-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold shadow hover:opacity-90 flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Choose Section Template</span>
                </button>
              )}
            </div>
          )}
        </div>
      );
    }

    // Determine smart positioning of floating toolbar
    const isAtTopOfCanvas = childIndex === 0 && (!parentNode || parentNode.id === rootNode.id);

    // Standard Block Element
    return (
      <div key={node.id} className="relative group/block transition-all w-full">
        {/* Drop indicator Before Node */}
        {!isPreview && parentNode && (
          <div
            onDragOver={(e) => handleDragOver(e, parentNode.id, childIndex, 'before')}
            onDragLeave={() => setActiveDrop(null)}
            onDrop={(e) => handleDrop(e, parentNode.id, childIndex)}
            className={`transition-all relative z-20 flex items-center justify-center ${
              isDropBefore ? 'h-6 my-1 bg-[#62c462]/10 border-y-2 border-[#62c462]' : 'h-1.5 -my-0.5 hover:h-3'
            }`}
          >
            {isDropBefore && (
              <span className="text-[11px] font-bold px-2 py-0.5 bg-[#62c462] text-white rounded-full shadow-md flex items-center gap-1">
                <Plus className="h-3 w-3" /> Drop Here
              </span>
            )}
          </div>
        )}

        {/* The Node Container */}
        <div
          data-node-id={node.id}
          data-wb-node-id={node.id}
          draggable={!isPreview && isSelected && !node.locked}
          onDragStart={(e) => {
            if (isPreview || node.locked) return;
            e.stopPropagation();
            e.dataTransfer.setData(
              'application/json',
              JSON.stringify({ kind: 'move_block', sourceId: node.id })
            );
            e.dataTransfer.effectAllowed = 'move';
          }}
          onClick={(e) => {
            if (isPreview) return;
            e.stopPropagation();
            onSelectNode(node.id);
          }}
          onDoubleClick={(e) => {
            if (isPreview) return;
            e.stopPropagation();
            startInlineEdit(node);
          }}
          className={`relative transition-all ${
            isPreview
              ? ''
              : isSelected
              ? 'outline outline-2 -outline-offset-2 outline-[#3b97e3] shadow-md'
              : showOutlines
              ? 'outline outline-1 outline-dashed outline-[#3b97e3]/40 -outline-offset-1 hover:outline-[#3b97e3]/80'
              : 'hover:outline hover:outline-1 hover:-outline-offset-1 hover:outline-[#3b97e3]/40'
          } ${node.hidden && !isPreview ? 'opacity-40 grayscale-[20%]' : ''}`}
        >
          {/* Floating Action Toolbar on Selected Node (Smart Top or Inside Placement) */}
          {isSelected && !isPreview && (
            <div
              className={`absolute z-40 bg-card/95 backdrop-blur-md border border-border rounded-lg shadow-xl px-2 py-1 flex items-center gap-1 text-xs text-foreground select-none animate-in fade-in duration-100 ${
                isAtTopOfCanvas ? 'top-2 right-2' : '-top-10 left-2'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle */}
              <div
                title="Drag to reorder"
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
              >
                <GripVertical className="h-3.5 w-3.5" />
              </div>

              {/* Block Type Badge (Human-Friendly Title) */}
              <div className="font-bold text-[11px] bg-primary text-primary-foreground px-2 py-0.5 rounded shadow-xs max-w-[130px] truncate">
                {blockTitle}
              </div>

              <div className="h-3.5 w-[1px] bg-border mx-0.5" />

              {/* Move Up */}
              <button
                type="button"
                title="Move Up"
                disabled={childIndex === 0}
                onClick={() => onMoveNode && onMoveNode(node.id, 'up')}
                className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none transition-colors"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>

              {/* Move Down */}
              <button
                type="button"
                title="Move Down"
                disabled={!parentNode?.children || childIndex === parentNode.children.length - 1}
                onClick={() => onMoveNode && onMoveNode(node.id, 'down')}
                className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none transition-colors"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>

              <div className="h-3.5 w-[1px] bg-border mx-0.5" />

              {/* Quick Inline Edit */}
              <button
                type="button"
                title="Edit text inline"
                onClick={() => startInlineEdit(node)}
                className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors flex items-center gap-0.5"
              >
                <Edit3 className="h-3 w-3" />
                <span className="text-[10px] hidden sm:inline">Text</span>
              </button>

              {/* Duplicate */}
              {onDuplicateNode && (
                <button
                  type="button"
                  title="Duplicate block"
                  onClick={() => onDuplicateNode(node.id)}
                  className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}

              {/* Wrap in Container */}
              {onWrapInContainer && (
                <button
                  type="button"
                  title="Wrap in container"
                  onClick={() => onWrapInContainer(node.id)}
                  className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
                >
                  <FolderPlus className="h-3.5 w-3.5" />
                </button>
              )}

              <div className="h-3.5 w-[1px] bg-border mx-0.5" />

              {/* Delete */}
              {onDeleteNode && (
                <button
                  type="button"
                  title="Delete block"
                  onClick={() => onDeleteNode(node.id)}
                  className="p-1 hover:bg-rose-500/10 rounded text-rose-500 hover:text-rose-600 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {/* Inline Edit Popover / Box */}
          {isEditingInline && (
            <div
              className="absolute inset-x-2 -top-14 z-50 bg-card border-2 border-primary rounded-xl shadow-2xl p-3 flex flex-col gap-2 animate-in fade-in zoom-in-95 duration-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
                <span>Quick Text Edit ({inlinePropName})</span>
                <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono">Press Enter to save</span>
              </div>
              <input
                type="text"
                autoFocus
                value={inlineTextValue}
                onChange={(e) => setInlineTextValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    commitInlineEdit();
                  } else if (e.key === 'Escape') {
                    setInlineEditingNodeId(null);
                  }
                }}
                className="w-full p-2 text-sm rounded-lg border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-medium"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setInlineEditingNodeId(null)}
                  className="px-2.5 py-1 text-xs rounded border border-border hover:bg-muted text-muted-foreground cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={commitInlineEdit}
                  className="px-3 py-1 text-xs font-bold rounded bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1 cursor-pointer"
                >
                  <Check className="h-3 w-3" /> Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Render Component Content */}
          <div
            className="wb-block-content w-full"
            style={resolveCanvasNodeStyle(node, viewport, activeState, isSelected)}
          >
            <Renderer
              node={node}
              isEditor={!isPreview}
              selectedId={selectedNodeId}
              onSelectNode={onSelectNode}
              onUpdateProps={onUpdateProps}
            >
              {renderedChildren}
            </Renderer>
          </div>

          {/* Empty Nested Container Drop Zone */}
          {isContainerLike && (!node.children || node.children.length === 0) && !isPreview && (
            <div
              onDragOver={(e) => handleDragOver(e, node.id, 0, 'inside')}
              onDragLeave={() => setActiveDrop(null)}
              onDrop={(e) => handleDrop(e, node.id, 0)}
              className="m-2 p-6 border border-dashed border-border rounded-lg text-center bg-muted/20 text-xs text-muted-foreground flex flex-col items-center justify-center gap-1.5"
            >
              <Plus className="h-4 w-4 text-primary/60" />
              <span>Drop blocks into this {blockTitle}</span>
            </div>
          )}
        </div>

        {/* Drop indicator After Node */}
        {!isPreview && parentNode && (
          <div
            onDragOver={(e) => handleDragOver(e, parentNode.id, childIndex + 1, 'after')}
            onDragLeave={() => setActiveDrop(null)}
            onDrop={(e) => handleDrop(e, parentNode.id, childIndex + 1)}
            className={`transition-all relative z-20 flex items-center justify-center ${
              isDropAfter ? 'h-6 my-1 bg-[#62c462]/10 border-y-2 border-[#62c462]' : 'h-1.5 -my-0.5 hover:h-3'
            }`}
          >
            {isDropAfter && (
              <span className="text-[11px] font-bold px-2 py-0.5 bg-[#62c462] text-white rounded-full shadow-md flex items-center gap-1">
                <Plus className="h-3 w-3" /> Drop Here
              </span>
            )}
          </div>
        )}

        {/* Contextual "+ Add Section" divider between top-level sections */}
        {!isPreview && parentNode?.id === rootNode.id && onOpenSectionPicker && (
          <div className="relative group/divider py-1 flex items-center justify-center">
            <div className="absolute inset-x-0 h-[1px] bg-transparent group-hover/divider:bg-primary/30 transition-colors" />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSectionPicker(childIndex + 1);
              }}
              className="relative z-10 opacity-0 group-hover/divider:opacity-100 transition-all scale-90 group-hover/divider:scale-100 px-3 py-1 rounded-full bg-primary text-primary-foreground shadow-md text-xs font-semibold flex items-center gap-1 cursor-pointer hover:bg-primary/90"
            >
              <Plus className="h-3 w-3" />
              <span>Add Section</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Top Pixel Rulers */}
      {!isPreview && (
        <CanvasRulers
          zoom={zoom}
          canvasWidth={viewport === 'desktop' ? 1200 : viewport === 'tablet' ? 768 : 375}
        />
      )}
      <style id="wb-canvas-dynamic-states">{generateCanvasDynamicCSS(rootNode)}</style>

      {/* Scrollable Stage Area */}
      <main
        className="flex-1 bg-muted/20 overflow-auto p-3 sm:p-6 md:p-8 flex flex-col justify-start items-center select-none relative"
        onClick={() => {
          if (!isPreview) onSelectNode('');
        }}
      >
        {/* The Responsive Stage Frame (True Edge-to-Edge Browser Viewport) */}
        <div
          className={`${isPreview && viewport === 'desktop' ? 'w-full' : VIEWPORT_WIDTHS[viewport]} min-h-[720px] bg-background border ${isPreview ? 'border-none rounded-none shadow-none' : 'border-border/80 rounded-xl shadow-2xl'} overflow-hidden flex flex-col transition-all duration-300 relative my-auto wb-artboard-container`}
          style={{
            transform: zoom !== 1 ? `scale(${zoom})` : undefined,
            transformOrigin: 'top center',
            containerType: 'inline-size',
            containerName: 'wbcanvas',
          }}
        >
          {/* Canvas Tools Overlay: GrapesJS Selection Frame, Resizer, Toolbar, Box-Model Overlays */}
          {!isPreview && (
            <CanvasToolsOverlay
              selectedNode={selectedNode}
              parentNode={parentNode}
              activeState={activeState}
              showOutlines={showOutlines}
              showSpacingGuides={showSpacingGuides}
              highlightedBoxPart={highlightedBoxPart}
              zoom={zoom}
              onSelectParent={() => {
                if (parentNode) onSelectNode(parentNode.id);
              }}
              onMoveNode={(dir) => {
                if (selectedNodeId && onMoveNode) onMoveNode(selectedNodeId, dir);
              }}
              onDuplicateNode={() => {
                if (selectedNodeId && onDuplicateNode) onDuplicateNode(selectedNodeId);
              }}
              onWrapInContainer={() => {
                if (selectedNodeId && onWrapInContainer) onWrapInContainer(selectedNodeId);
              }}
              onDeleteNode={() => {
                if (selectedNodeId && onDeleteNode) onDeleteNode(selectedNodeId);
              }}
              onResizeNode={(patch) => {
                if (selectedNodeId && onResizeNode) onResizeNode(selectedNodeId, patch);
              }}
            />
          )}

          {/* Simulated Browser Chrome Topbar (Only in Edit Mode) */}
          {!isPreview && (
            <div className="h-9 shrink-0 bg-muted/80 border-b border-border px-3.5 flex items-center justify-between select-none">
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
                <div className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1 bg-background/80 border border-border/60 rounded-md text-[11px] font-mono text-muted-foreground max-w-sm w-full justify-center">
                <Lock className="h-3 w-3 text-emerald-500" />
                <span className="truncate">https://{siteSlug || 'academy'}.sites.thesci.co{pagePath || '/'}</span>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={`/s/${siteSlug || 'academy'}${pagePath || '/'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open Live Preview in New Tab"
                  className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground text-[10px] font-semibold flex items-center gap-1 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="hidden sm:inline">Live</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}

          {/* Browser Canvas Content Body */}
          <div className="flex-1 flex flex-col overflow-x-hidden bg-background">
            {renderNode(rootNode, null, 0)}
          </div>
        </div>
      </main>

      {/* Pinned Bottom Status Bar & Hierarchy Breadcrumbs (Only in Edit Mode) */}
      {!isPreview && (
        <footer className="h-8 shrink-0 border-t border-border bg-card px-4 flex items-center justify-between text-xs select-none z-20">
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar text-muted-foreground py-0.5">
            <span className="flex items-center gap-1 font-semibold text-foreground text-[11px] shrink-0">
              <Layers className="h-3 w-3 text-primary" /> Hierarchy:
            </span>
            {breadcrumbNodes.length > 0 ? (
              breadcrumbNodes.map((item, idx) => {
                const itemTitle = BLOCK_MANIFESTS[item.type as keyof typeof BLOCK_MANIFESTS]?.title || item.type;
                return (
                  <React.Fragment key={item.id}>
                    {idx > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/60 shrink-0" />}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectNode(item.id);
                      }}
                      className={`px-2 py-0.5 rounded text-[11px] transition-colors cursor-pointer shrink-0 ${
                        item.id === selectedNodeId
                          ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                          : 'hover:bg-muted text-foreground/80 hover:text-foreground'
                      }`}
                    >
                      {itemTitle}
                    </button>
                  </React.Fragment>
                );
              })
            ) : (
              <span className="text-[11px] text-muted-foreground/80">Click any block on canvas to inspect</span>
            )}
          </div>

          <div className="flex items-center gap-3 text-[11px] text-muted-foreground shrink-0 pl-3">
            <span>{rootNode.children?.length || 0} sections</span>
            <span className="font-mono">{viewport === 'desktop' ? '1200px' : viewport === 'tablet' ? '768px' : '375px'}</span>
          </div>
        </footer>
      )}
    </div>
  );
}
